// 예산: an amount per month and top-level category (children too, when
// wanted), and "지난달 예산 가져오기" (budgets/copy: only categories that
// have no budget this month yet). Enter (a phone keyboard's 다음) goes on to
// the next amount, the keyboard staying up; an amount is written when it
// is left.
import { type FormEvent, useEffect, useState } from 'react'

import { errorText } from '../../api/client'
import { ErrorText } from '../../ui/common'
import { showToast } from '../../ui/toast'
import type { Id, TensorItem } from '../api'
import { engine } from '../engine'
import { addMonths, amountInput, monthTitle, parseAmount, won } from '../format'
import { budgetOf, itemsOf } from '../views'
import { AmountField, type Current, MonthSwitch } from './common'

function BudgetLine({ cur, month, item, sub, last }: { cur: Current; month: string; item: TensorItem; sub?: boolean; last: boolean }) {
  const amount = budgetOf(cur.book?.budgets ?? {}, month, item.id)
  const [text, setText] = useState(amount ? amountInput(String(amount)) : '')
  const [busy, setBusy] = useState(false)
  const parent = item.parent_id ? (cur.book?.items[item.parent_id]?.name ?? '') : ''
  // a change from elsewhere (or another month) shows when not being typed
  useEffect(() => setText(amount ? amountInput(String(amount)) : ''), [amount, month])

  /** Enter: on to the next amount (leaving this one writes it), or away from the last. */
  function next(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const input = e.currentTarget.querySelector('input')
    const all = Array.from(document.querySelectorAll<HTMLInputElement>('.t-budget-line input'))
    const after = input ? all[all.indexOf(input) + 1] : undefined
    if (after) after.focus()
    else input?.blur()
  }

  async function save() {
    const n = text === '' ? 0 : parseAmount(text)
    if (n === null || n === amount || busy) return
    setBusy(true)
    try {
      await engine.setBudget(cur.workbook.id, month, item.id, n)
    } catch (err) {
      showToast(errorText(err))
      setText(amount ? amountInput(String(amount)) : '')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className={`t-budget-line${sub ? ' sub' : ''}`} onSubmit={next} onBlur={() => void save()}>
      <AmountField label={sub ? `${parent} > ${item.name}` : item.name} value={text} onChange={setText} enterKeyHint={last ? 'done' : 'next'} />
    </form>
  )
}

export function BudgetsScreen({ cur, month, setMonth }: { cur: Current; month: string; setMonth: (m: string) => void }) {
  const items = cur.book?.items ?? {}
  const tops = itemsOf(items, 'category')
  const [open, setOpen] = useState<Set<Id>>(() => new Set())
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  let total = 0
  for (const t of tops) total += budgetOf(cur.book?.budgets ?? {}, month, t.id)
  // the last amount on the screen (its key is 완료)
  const lastTop = tops[tops.length - 1]
  const lastKids = lastTop && open.has(lastTop.id) ? itemsOf(items, 'category', lastTop.id) : []
  const lastId = lastKids[lastKids.length - 1]?.id ?? lastTop?.id

  async function copy() {
    setBusy(true)
    setError(null)
    try {
      const n = await engine.copyBudgets(cur.workbook.id, addMonths(month, -1), month)
      showToast(n > 0 ? `${monthTitle(addMonths(month, -1))} 예산 ${n}개를 가져왔습니다.` : '가져올 예산이 없습니다.')
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page t-page">
      <MonthSwitch month={month} onChange={setMonth} />
      <section className="card">
        <h3>분류별 예산</h3>
        <p className="muted small">
          빈칸이나 0은 예산 없음입니다. 위 분류에 예산이 없으면 세부 분류 예산의 합을 씁니다. 합계 {won(total)}
        </p>
        <button type="button" className="secondary" disabled={busy} onClick={() => void copy()}>
          지난달 예산 가져오기
        </button>
        <ErrorText text={error} />
        {!cur.book || cur.book.loading ? null : tops.length === 0 ? (
          <p className="muted">분류가 없습니다. 항목 관리에서 더해 주세요.</p>
        ) : (
          tops.map((t) => {
            const children = itemsOf(items, 'category', t.id)
            const shown = open.has(t.id)
            return (
              <div key={t.id} className="t-budget-group">
                <BudgetLine cur={cur} month={month} item={t} last={t.id === lastId} />
                {children.length > 0 && (
                  <button
                    type="button"
                    className="link small"
                    aria-expanded={shown}
                    onClick={() =>
                      setOpen((s) => {
                        const n = new Set(s)
                        if (n.has(t.id)) n.delete(t.id)
                        else n.add(t.id)
                        return n
                      })
                    }
                  >
                    {shown ? '세부 분류 접기' : `${t.name} 세부 분류 예산`}
                  </button>
                )}
                {shown && children.map((c) => <BudgetLine key={c.id} cur={cur} month={month} item={c} sub last={c.id === lastId} />)}
              </div>
            )
          })
        )}
      </section>
    </div>
  )
}
